import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Filter, TriangleAlert } from "lucide-react";
import { CHART, ChartCard, ChartEmpty, TooltipBox, axisTick } from "./ui";

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload || {};
  return (
    <TooltipBox title={d.stage}>
      <span>{d.count} sessions</span>
      {d.pct !== undefined && <span className="text-primary">{d.pct}% of booking sessions</span>}
    </TooltipBox>
  );
};

const BookingFunnelChart = ({ funnel }: any) => {
  const f = funnel || {};
  const card = (body) => (
    <ChartCard icon={<Filter />} title="Booking Conversion Funnel" description="How many guests reached each step of booking.">{body}</ChartCard>
  );
  if (!f.booking_sessions) {
    return card(<ChartEmpty icon={<Filter />} text="No booking sessions in selected period" />);
  }

  const bs = f.booking_sessions || 1;
  const stages = [
    { stage: "Booking Started", count: f.booking_started || 0 },
    { stage: "Guest Details", count: f.guest_details_collected || 0 },
    { stage: "Date Selection", count: f.dates_selected || 0 },
    { stage: "Rooms Shown", count: f.rooms_shown || 0 },
    { stage: "Offer Selected", count: f.offer_selected || 0 },
    { stage: "Guest Count", count: f.guest_count_collected || 0 },
    ...(f.billing_address_collected ? [{ stage: "Billing Address", count: f.billing_address_collected }] : []),
    { stage: "Summary Shown", count: f.summary_shown || 0 },
    { stage: "Confirmed", count: f.booking_confirmed || 0 },
  ];
  const chartData = stages.map((s) => ({ ...s, pct: ((s.count / bs) * 100).toFixed(1) }));
  const chartHeight = Math.max(200, stages.length * 45);
  const last = chartData.length - 1;

  return card(
    <>
      <div className="funnel-stats-row flex flex-wrap items-center gap-2 text-sm">
        <span className="funnel-stat rounded-full bg-secondary px-3 py-1 text-secondary-foreground">
          <strong>{f.booking_sessions}</strong> booking sessions out of <strong>{f.total_sessions}</strong> total
        </span>
        {f.no_availability > 0 && (
          <span className="funnel-stat funnel-stat-warn inline-flex items-center gap-1 rounded-full bg-warning-500/15 px-3 py-1 text-warning-700">
            <TriangleAlert className="size-3.5" aria-hidden="true" />
            {f.no_availability} got no availability
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
          <XAxis type="number" tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="stage" tick={{ fontSize: 12, fill: CHART.text }} tickLine={false} axisLine={false} width={120} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "var(--accent)" }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={28}>
            {chartData.map((_, idx) => (
              <Cell key={idx} fill={idx === last ? CHART.success : CHART.primary} fillOpacity={idx === last ? 1 : 1 - (idx / last) * 0.55} />
            ))}
            <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: CHART.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default BookingFunnelChart;
